import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getStoryById, createStory, updateStory } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Save } from 'lucide-react';

const StoryEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    category: 'fiction',
    date: new Date().toISOString().split('T')[0],
    readTime: '',
    content: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetchStory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStory = async () => {
    try {
      const data = await getStoryById(id);
      setFormData({
        title: data.title,
        excerpt: data.excerpt,
        category: data.category,
        date: data.date,
        readTime: data.readTime,
        content: data.content
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load story",
        variant: "destructive"
      });
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await updateStory(id, formData);
        toast({
          title: "Story updated",
          description: "Your changes have been saved successfully."
        });
      } else {
        await createStory(formData);
        toast({
          title: "Story created",
          description: "Your new story has been published."
        });
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save story. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <h1 className="text-2xl font-serif text-gray-900">
            {isEditing ? 'Edit Story' : 'Create New Story'}
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="title" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Title *
              </label>
              <Input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter story title"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="fiction">Fiction</option>
                <option value="article">Article</option>
              </select>
            </div>

            <div>
              <label htmlFor="readTime" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Read Time *
              </label>
              <Input
                type="text"
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                required
                placeholder="e.g., 5 min read"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="date" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Date *
              </label>
              <Input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="excerpt" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Excerpt *
              </label>
              <Textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Brief excerpt or summary (1-2 sentences)"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="content" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Content *
              </label>
              <Textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={20}
                placeholder="Write your story or article content here..."
                className="font-serif"
              />
              <p className="text-xs text-gray-500 mt-2">
                Tip: Use double line breaks to separate paragraphs
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
            <Button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
            >
              <Save size={16} />
              {saving ? 'Saving...' : (isEditing ? 'Update Story' : 'Create Story')}
            </Button>
            <Link to="/admin/dashboard">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoryEditor;